import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button, buttonClasses } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'

import { useAdminMutation } from '../api'
import type { AdminEvent, Readiness } from '../types'

type Transition = 'publish' | 'unpublish' | 'archive'

/**
 * Brouillon → publiée → close → archivée, avec les règles du serveur.
 *
 * Seuls les gestes permis par le statut ACTUEL apparaissent : un bouton
 * « Publier » sur une édition archivée promettrait ce que le serveur refuse.
 * Chacun passe par un encadré de confirmation qui dit ce qui va se passer —
 * dans la page, pas dans une boîte `confirm()` que le téléphone affiche mal et
 * qu'on valide sans la lire.
 *
 * La clôture n'est pas refaite ici : l'écran dédié liste ce qui reste à régler
 * (caisses non comptées, paiements en attente) et porte la clôture forcée.
 * Deux chemins pour le même geste finiraient par appliquer deux règles.
 */
export function EditionLifecycle({
  event,
  readiness,
  can,
}: {
  event: AdminEvent
  readiness: Readiness | undefined
  can: (permission: string) => boolean
}) {
  const { t } = useTranslation()
  const [asked, setAsked] = useState<Transition | null>(null)

  // Le chemin dépend du geste : une seule mutation, donc un seul état
  // « en cours » et une seule erreur à afficher. Ces routes n'ont pas de
  // corps ; celui qu'on envoie est ignoré.
  const transition = useAdminMutation<AdminEvent, { action: Transition }>(
    'post',
    ({ action }) => `/admin/events/${event.id}/${action}`,
    event.id,
    [
      ['admin-editions'],
      ['admin-event', event.id],
      ['admin-readiness', event.id],
      ['admin-alerts', event.id],
      ['public-event'],
    ],
  )

  const mayPublish = can('event.event.publish')
  const mayClose = can('event.event.close')
  const status = event.status

  const actions: Transition[] = []
  if (status === 'draft' && mayPublish) actions.push('publish')
  if (status === 'published' && mayPublish) actions.push('unpublish')
  if ((status === 'draft' || status === 'closed') && mayClose) actions.push('archive')
  const showClose = status === 'published' && mayClose

  if (actions.length === 0 && !showClose && status !== 'archived') return null

  // La checklist dit pourquoi « Publier » est éteint : un bouton grisé sans
  // raison fait chercher, et finit en appel à l'API à la main.
  const blockers = readiness?.steps.filter((step) => step.blocking && !step.done) ?? []
  const publishBlocked = readiness ? !readiness.can_publish : true

  return (
    <div className="rounded-md border border-line-subtle p-4">
      <h3 className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.editions.lifecycle.title')}
      </h3>
      <p className="mt-1 text-sm text-content-secondary">
        {t(`admin.editions.lifecycle.status.${status}`)}
      </p>

      {status === 'draft' && mayPublish && blockers.length > 0 ? (
        <div className="mt-3 rounded-md border border-state-warning/30 bg-state-warning/5 px-3 py-2">
          <p className="text-xs text-content-secondary">
            {t('admin.editions.lifecycle.blockedIntro')}
          </p>
          <ul className="mt-1.5 space-y-1">
            {blockers.map((step) => (
              <li key={step.key} className="text-xs text-content-primary">
                · {t(`admin.readiness.step.${step.key}`, { defaultValue: step.label })}
                {step.detail ? (
                  <span className="text-content-muted"> — {step.detail}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {asked ? (
        <div
          className="mt-3 rounded-md border border-champagne-400/40 bg-bg-base/60 p-3"
          role="alertdialog"
          aria-label={t(`admin.editions.lifecycle.${asked}.action`)}
        >
          <p className="text-sm font-medium text-content-primary">
            {t(`admin.editions.lifecycle.${asked}.confirmTitle`, { slug: event.slug })}
          </p>
          <p className="mt-1 text-sm text-content-secondary">
            {t(`admin.editions.lifecycle.${asked}.explain`)}
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Button
              size="sm"
              disabled={transition.isPending || (asked === 'publish' && publishBlocked)}
              onClick={() =>
                transition.mutate({ action: asked }, { onSuccess: () => setAsked(null) })
              }
            >
              {transition.isPending
                ? t('state.loading')
                : t(`admin.editions.lifecycle.${asked}.confirm`)}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAsked(null)
                transition.reset()
              }}
            >
              {t('admin.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {actions.map((action) => (
            <Button
              key={action}
              size="sm"
              variant={action === 'publish' ? 'ember' : 'outline'}
              disabled={action === 'publish' && publishBlocked}
              onClick={() => {
                transition.reset()
                setAsked(action)
              }}
            >
              {t(`admin.editions.lifecycle.${action}.action`)}
            </Button>
          ))}
          {showClose ? (
            <Link to="/admin/cloture" className={buttonClasses({ variant: 'outline', size: 'sm' })}>
              {t('admin.editions.lifecycle.close.action')}
            </Link>
          ) : null}
        </div>
      )}

      {showClose && !asked ? (
        <p className="mt-2 text-xs text-content-muted">{t('admin.editions.lifecycle.close.explain')}</p>
      ) : null}

      {transition.error ? (
        <p className="mt-3 text-sm text-state-danger" role="alert">
          {transition.error instanceof ApiError
            ? transition.error.message
            : t('admin.editions.genericError')}
        </p>
      ) : null}
    </div>
  )
}
